import { useCallback, useState } from 'react';

import { Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { DeleteDialog } from 'common/components/deleteDialog/DeleteDialog';
import { setDialogOpen } from 'common/components/deleteDialog/deleteDialogSlice';
import { selectPageInfo, setRows } from 'common/components/PalmDataGrid/palmDataGridSlice';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import { deleteAuthorizedIssuer } from 'common/services/api/AuthorizedIssuers/AuthorizedIssuers.thunk';
import { DeleteDialogContentProps } from 'common/types/TypeDefs';
import { handleDeleteDialogError } from 'common/utils/errorHandlers';
import { useSnackbar } from 'notistack';

export function AuthorizedIssuerDeleteDialogContent({ itemId = '', editingRow, title }: Readonly<DeleteDialogContentProps<{ identityProviderName: string }>>) {
    const { identityProviderName } = editingRow ?? {};
    const { enqueueSnackbar } = useSnackbar();
    const [processing, setProcessing] = useState<boolean>(false);
    const pageInfo = useAppSelector(selectPageInfo);
    const [tenantId, identityproviderId, audience] = itemId.toString().split('.');

    const successHandler = (): void => {
        dispatch(setDialogOpen(false));
        enqueueSnackbar('Authorized Issuer has been deleted.', { variant: 'success' });
    };

    const dispatch = useAppDispatch();
    const authorizedIssuerDeleteCallback = useCallback(() => {
        if (itemId) {
            setProcessing(true);
            dispatch(deleteAuthorizedIssuer({
                audience,
                authorizedIssuerId: identityproviderId,
                tenantId
            }))
                .unwrap()
                .then((result) => {
                    dispatch(setRows(result.rows));
                    successHandler();
                })
                .catch((error) => {
                    handleDeleteDialogError('Authorized Issuer', enqueueSnackbar, extractStatusCode(error.message));
                })
                .finally(() => {
                    setProcessing(false);
                });
        }
    }, [itemId]);

    return (
        <DeleteDialog deleteCallback={authorizedIssuerDeleteCallback} processing={processing ?? false} title={title}>
            <Typography color="info.light" fontSize='0.9rem' fontWeight='550'>
                Identity Provider Name: <Typography component="span" color="text.primary" fontWeight="400">{identityProviderName}</Typography>
            </Typography>
            <Typography color="info.light" fontSize='0.9rem' fontWeight='550'>
                Authorized Issuer Audience: <Typography component="span" color="text.primary" fontWeight="400">{audience}</Typography>
            </Typography>
            <Typography color="info.light" fontSize='0.9rem' fontWeight='550'>
                Tenant Name: <Typography component="span" color="text.primary" fontWeight="400">{pageInfo?.name}</Typography>
            </Typography>
        </DeleteDialog>
    );
}
